'use client';

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { LOCAL_STORAGE_KEYS } from '@/shared/constants/storage-keys';
import { getLocalStorageItem, setLocalStorageItem } from '@/shared/utils/storage';

import type { AudioState, HLSPlayerContext, PlaybackSpeed, PlaybackState, UIState } from '../types';

const VALID_PLAYBACK_SPEEDS: PlaybackSpeed[] = [0.5, 0.75, 1, 1.25, 1.5, 2];

function getStoredPlaybackSpeed(): PlaybackSpeed {
	const stored = getLocalStorageItem(LOCAL_STORAGE_KEYS.VIDEO_PLAYBACK_SPEED);
	if (!stored || typeof stored !== 'string') return 1;

	const parsed = parseFloat(stored);
	if (VALID_PLAYBACK_SPEEDS.includes(parsed as PlaybackSpeed)) {
		return parsed as PlaybackSpeed;
	}
	return 1;
}

const HLSPlayerContext = createContext<HLSPlayerContext | null>(null);

type HLSPlayerProviderProps = {
	children: React.ReactNode;
	onDownloadVideoClick?: () => Promise<void>;
	videoRef: React.RefObject<HTMLVideoElement | null>;
	containerRef: React.RefObject<HTMLDivElement | null>;
};

export function HLSPlayerProvider({
	children,
	videoRef,
	containerRef,
	onDownloadVideoClick,
}: HLSPlayerProviderProps) {
	const [playbackState, setPlaybackState] = useState<PlaybackState>(() => ({
		isPlaying: false,
		currentTime: 0,
		duration: 0,
		playbackSpeed: getStoredPlaybackSpeed(),
	}));

	const [audioState, setAudioState] = useState<AudioState>({
		volume: 100,
		isMuted: false,
	});

	const [uiState, setUIState] = useState<UIState>({
		isFullscreen: false,
		showControls: true,
	});

	const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Auto-hide controls logic
	const handleMouseMove = useCallback(() => {
		setUIState((prev) => ({ ...prev, showControls: true }));

		if (controlsTimeoutRef.current) {
			clearTimeout(controlsTimeoutRef.current);
		}

		// Only auto-hide if video is playing
		if (playbackState.isPlaying) {
			controlsTimeoutRef.current = setTimeout(() => {
				setUIState((prev) => ({ ...prev, showControls: false }));
			}, 3000);
		}
	}, [playbackState.isPlaying]);

	const handleMouseLeave = useCallback(() => {
		if (controlsTimeoutRef.current) {
			clearTimeout(controlsTimeoutRef.current);
		}
		// Don't hide controls when paused
		if (playbackState.isPlaying) {
			setUIState((prev) => ({ ...prev, showControls: false }));
		}
	}, [playbackState.isPlaying]);

	// Video control methods
	const togglePlay = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;

		if (video.paused) {
			video.play();
		} else {
			video.pause();
		}
	}, [videoRef]);

	const seek = useCallback(
		(time: number) => {
			const video = videoRef.current;
			if (!video) return;

			video.currentTime = time;
			setPlaybackState((prev) => ({ ...prev, currentTime: time }));
		},
		[videoRef]
	);

	const setVolume = useCallback(
		(newVolume: number) => {
			const video = videoRef.current;
			if (!video) return;

			const clampedVolume = Math.max(0, Math.min(100, newVolume));
			video.volume = clampedVolume / 100;

			// Unmute if volume is increased from 0
			setAudioState((prev) => ({
				volume: clampedVolume,
				isMuted: clampedVolume > 0 ? false : prev.isMuted,
			}));

			if (clampedVolume > 0 && audioState.isMuted) {
				video.muted = false;
			}
		},
		[videoRef, audioState.isMuted]
	);

	const toggleMute = useCallback(() => {
		const video = videoRef.current;
		if (!video) return;

		video.muted = !video.muted;
		setAudioState((prev) => ({ ...prev, isMuted: !video.muted }));
	}, [videoRef]);

	const changeSpeed = useCallback(
		(speed: PlaybackSpeed) => {
			const video = videoRef.current;
			if (!video) return;

			video.playbackRate = speed;
			setPlaybackState((prev) => ({ ...prev, playbackSpeed: speed }));

			// Persist to localStorage
			setLocalStorageItem(LOCAL_STORAGE_KEYS.VIDEO_PLAYBACK_SPEED, String(speed));
		},
		[videoRef]
	);

	const toggleFullscreen = useCallback(() => {
		const container = containerRef.current;
		if (!container) return;

		// iPhone Safari has no element fullscreen, only native fullscreen on the video itself
		const video = videoRef.current as
			| (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
			| null;
		if (!container.requestFullscreen) {
			video?.webkitEnterFullscreen?.();
			return;
		}

		if (!document.fullscreenElement) {
			container.requestFullscreen().catch((err) => {
				console.error('Error attempting to enable fullscreen:', err);
			});
		} else {
			document.exitFullscreen();
		}
	}, [containerRef, videoRef]);

	// Sync video events
	useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		let isResolvingDuration = false;

		const handlePlay = () => {
			setPlaybackState((prev) => ({ ...prev, isPlaying: true }));
		};

		const handlePause = () => {
			setPlaybackState((prev) => ({ ...prev, isPlaying: false }));
			setUIState((prev) => ({ ...prev, showControls: true })); // Always show controls when paused
		};

		const handleTimeUpdate = () => {
			setPlaybackState((prev) => ({ ...prev, currentTime: video.currentTime }));
		};

		const handleLoadedMetadata = () => {
			// Apply stored playback speed when video loads
			const storedSpeed = getStoredPlaybackSpeed();
			video.playbackRate = storedSpeed;

			// MediaRecorder webm files report an Infinity duration until seeked past the end
			if (!isFinite(video.duration)) {
				isResolvingDuration = true;
				video.currentTime = Number.MAX_SAFE_INTEGER;
			}

			setPlaybackState((prev) => ({
				...prev,
				duration: video.duration,
				playbackSpeed: storedSpeed,
			}));
			setAudioState({
				volume: video.volume * 100,
				isMuted: video.muted,
			});
		};

		const handleDurationChange = () => {
			if (!isFinite(video.duration)) return;

			if (isResolvingDuration) {
				isResolvingDuration = false;
				video.currentTime = 0;
			}
			setPlaybackState((prev) => ({ ...prev, duration: video.duration }));
		};

		const handleVolumeChange = () => {
			setAudioState({
				volume: video.volume * 100,
				isMuted: video.muted,
			});
		};

		const handleEnded = () => {
			// Fix for video stopping early: when video ends, set currentTime to duration
			setPlaybackState((prev) => ({
				...prev,
				isPlaying: false,
				currentTime: prev.duration,
			}));
			setUIState((prev) => ({ ...prev, showControls: true }));
		};

		video.addEventListener('play', handlePlay);
		video.addEventListener('pause', handlePause);
		video.addEventListener('timeupdate', handleTimeUpdate);
		video.addEventListener('loadedmetadata', handleLoadedMetadata);
		video.addEventListener('durationchange', handleDurationChange);
		video.addEventListener('volumechange', handleVolumeChange);
		video.addEventListener('ended', handleEnded);

		return () => {
			video.removeEventListener('play', handlePlay);
			video.removeEventListener('pause', handlePause);
			video.removeEventListener('timeupdate', handleTimeUpdate);
			video.removeEventListener('loadedmetadata', handleLoadedMetadata);
			video.removeEventListener('durationchange', handleDurationChange);
			video.removeEventListener('volumechange', handleVolumeChange);
			video.removeEventListener('ended', handleEnded);
		};
	}, [videoRef]);

	// Sync fullscreen state
	useEffect(() => {
		const handleFullscreenChange = () => {
			setUIState((prev) => ({ ...prev, isFullscreen: !!document.fullscreenElement }));
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);

		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, []);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (controlsTimeoutRef.current) {
				clearTimeout(controlsTimeoutRef.current);
			}
		};
	}, []);

	const contextValue = useMemo<HLSPlayerContext>(
		() => ({
			playbackState,
			audioState,
			uiState,
			videoRef,
			containerRef,
			togglePlay,
			seek,
			setVolume,
			toggleMute,
			changeSpeed,
			toggleFullscreen,
			handleMouseMove,
			handleMouseLeave,
			onDownloadVideoClick,
		}),
		[
			playbackState,
			audioState,
			uiState,
			videoRef,
			containerRef,
			togglePlay,
			seek,
			setVolume,
			toggleMute,
			changeSpeed,
			toggleFullscreen,
			handleMouseMove,
			handleMouseLeave,
			onDownloadVideoClick,
		]
	);

	return <HLSPlayerContext.Provider value={contextValue}>{children}</HLSPlayerContext.Provider>;
}

export function useHLSPlayer(): HLSPlayerContext {
	const context = useContext(HLSPlayerContext);
	if (!context) {
		throw new Error('useHLSPlayer must be used within HLSPlayerProvider');
	}
	return context;
}
